import { calculateEma } from "./ema.js";
import { calculateSma } from "./sma.js";

export function calculateIndicator(definition, instance, data) {
  if (!definition) throw new Error("The indicator definition is unavailable.");
  if (!data?.length) return {};
  const inputs = instance.inputs || {};
  const source = resolvePriceSource(data, inputs.source || "close");

  switch (definition.id) {
    case "sma":
      return {
        value: offsetSeries(
          calculateSma(source, integer(inputs.length, 20)),
          signedInteger(inputs.offset, 0)
        )
      };
    case "ema":
      return { value: calculateEma(source, integer(inputs.length, 50)) };
    case "volume":
      return { volume: data.volumes };
    case "rsi":
      return calculateRsi(source, inputs);
    case "macd":
      return calculateMacd(source, inputs);
    case "bollinger":
      return calculateBollinger(source, inputs);
    default:
      throw new Error(`${definition.name} is not supported by this chart runtime.`);
  }
}

export function resolvePriceSource(data, source) {
  if (source === "open") return data.opens;
  if (source === "high") return data.highs;
  if (source === "low") return data.lows;
  if (source === "close") return data.closes;
  if (!["hl2", "hlc3", "ohlc4"].includes(source)) return data.closes;

  const output = new Float64Array(data.length);
  for (let index = 0; index < data.length; index += 1) {
    if (source === "hl2") {
      output[index] = (data.highs[index] + data.lows[index]) / 2;
    } else if (source === "hlc3") {
      output[index] = (data.highs[index] + data.lows[index] + data.closes[index]) / 3;
    } else {
      output[index] = (
        data.opens[index]
        + data.highs[index]
        + data.lows[index]
        + data.closes[index]
      ) / 4;
    }
  }
  return output;
}

export function calculateRsi(source, inputs = {}) {
  const length = integer(inputs.length, 14);
  const upperLevel = finite(inputs.upper, 70);
  const lowerLevel = finite(inputs.lower, 30);
  const values = new Float64Array(source.length);
  values.fill(Number.NaN);

  if (source.length > length) {
    let averageGain = 0;
    let averageLoss = 0;
    for (let index = 1; index <= length; index += 1) {
      const change = source[index] - source[index - 1];
      averageGain += Math.max(0, change);
      averageLoss += Math.max(0, -change);
    }
    averageGain /= length;
    averageLoss /= length;
    values[length] = rsiValue(averageGain, averageLoss);

    for (let index = length + 1; index < source.length; index += 1) {
      const change = source[index] - source[index - 1];
      averageGain = ((averageGain * (length - 1)) + Math.max(0, change)) / length;
      averageLoss = ((averageLoss * (length - 1)) + Math.max(0, -change)) / length;
      values[index] = rsiValue(averageGain, averageLoss);
    }
  }

  const smoothing = String(inputs.smoothing || "none");
  const smoothed = smoothing === "sma"
    ? calculateSparseSma(values, Math.max(2, Math.round(length / 3)))
    : smoothing === "ema"
      ? calculateSparseEma(values, Math.max(2, Math.round(length / 3)))
      : values;
  return {
    value: smoothed,
    upper: constantSeries(source.length, upperLevel),
    lower: constantSeries(source.length, lowerLevel)
  };
}

export function calculateMacd(source, inputs = {}) {
  const fastLength = integer(inputs.fastLength, 12);
  const slowLength = integer(inputs.slowLength, 26);
  const signalLength = integer(inputs.signalLength, 9);
  if (fastLength >= slowLength) {
    throw new RangeError("Fast length must be less than slow length.");
  }

  const fast = calculateEma(source, fastLength);
  const slow = calculateEma(source, slowLength);
  const macd = new Float64Array(source.length);
  macd.fill(Number.NaN);
  for (let index = 0; index < source.length; index += 1) {
    if (Number.isFinite(fast[index]) && Number.isFinite(slow[index])) {
      macd[index] = fast[index] - slow[index];
    }
  }
  const signal = calculateSparseEma(macd, signalLength);
  const histogram = new Float64Array(source.length);
  histogram.fill(Number.NaN);
  for (let index = 0; index < source.length; index += 1) {
    if (Number.isFinite(macd[index]) && Number.isFinite(signal[index])) {
      histogram[index] = macd[index] - signal[index];
    }
  }
  return {
    macd,
    signal,
    histogram,
    zero: constantSeries(source.length, 0)
  };
}

export function calculateBollinger(source, inputs = {}) {
  const length = integer(inputs.length, 20);
  const deviation = finite(inputs.deviation, 2);
  const middle = calculateSma(source, length);
  const upper = new Float64Array(source.length);
  const lower = new Float64Array(source.length);
  upper.fill(Number.NaN);
  lower.fill(Number.NaN);

  let sum = 0;
  for (let index = 0; index < source.length; index += 1) {
    const value = source[index];
    sum += value;
    if (index >= length) {
      const removed = source[index - length];
      sum -= removed;
    }
    if (index < length - 1) continue;
    const mean = sum / length;
    let squaredDeviation = 0;
    for (let windowIndex = index - length + 1; windowIndex <= index; windowIndex += 1) {
      const difference = source[windowIndex] - mean;
      squaredDeviation += difference * difference;
    }
    const variance = squaredDeviation / length;
    const offset = Math.sqrt(variance) * deviation;
    upper[index] = mean + offset;
    lower[index] = mean - offset;
  }
  return { upper, middle, lower };
}

export function calculateSparseEma(values, period) {
  const output = new Float64Array(values.length);
  output.fill(Number.NaN);
  const multiplier = 2 / (period + 1);
  let seed = 0;
  let seedCount = 0;
  let previous = Number.NaN;
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (!Number.isFinite(value)) continue;
    if (!Number.isFinite(previous)) {
      seed += value;
      seedCount += 1;
      if (seedCount === period) {
        previous = seed / period;
        output[index] = previous;
      }
      continue;
    }
    previous = (value - previous) * multiplier + previous;
    output[index] = previous;
  }
  return output;
}

function calculateSparseSma(values, period) {
  const output = new Float64Array(values.length);
  output.fill(Number.NaN);
  const window = [];
  let sum = 0;
  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    if (!Number.isFinite(value)) continue;
    window.push(value);
    sum += value;
    if (window.length > period) sum -= window.shift();
    if (window.length === period) output[index] = sum / period;
  }
  return output;
}

function constantSeries(length, value) {
  const output = new Float64Array(length);
  output.fill(value);
  return output;
}

function offsetSeries(values, offset) {
  if (offset === 0) return values;
  const output = new Float64Array(values.length);
  output.fill(Number.NaN);
  for (let sourceIndex = 0; sourceIndex < values.length; sourceIndex += 1) {
    const targetIndex = sourceIndex + offset;
    if (targetIndex >= 0 && targetIndex < values.length) {
      output[targetIndex] = values[sourceIndex];
    }
  }
  return output;
}

function rsiValue(averageGain, averageLoss) {
  if (averageLoss === 0) return averageGain === 0 ? 50 : 100;
  const relativeStrength = averageGain / averageLoss;
  return 100 - 100 / (1 + relativeStrength);
}

function integer(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function signedInteger(value, fallback) {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : fallback;
}

function finite(value, fallback) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}
